import { useEffect, useState } from "react";
import { Mail, MailOpen, Reply, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";

export default function AdminInbox({ onUnread }) {
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/messages/${id}`);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleArchive = async (m) => {
    try {
      await api.patch(`/admin/messages/${m.id}/archive`);
      toast.success(m.archived ? "Moved back to inbox." : "Archived.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteMessage = async (m) => {
    if (confirmDeleteId !== m.id) {
      setConfirmDeleteId(m.id);
      setTimeout(() => setConfirmDeleteId((c) => (c === m.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Enquiry deleted.");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmDeleteId(null);
    }
  };

  const sendReply = async (m) => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/messages/${m.id}/reply`, { body: replyBody });
      if (r.data.sent) {
        toast.success(`Reply sent to ${m.email}.`);
      } else {
        toast.warning("Reply saved — but that email address could not be reached.");
      }
      setReplyTo(null);
      setReplyBody("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };

  const unread = messages.filter((m) => !m.read && !m.archived).length;
  const archivedCount = messages.filter((m) => m.archived).length;
  const shown = messages.filter((m) => (tab === "archived" ? m.archived : !m.archived));

  // keep the Office tab badge in step with every read/archive/reply
  useEffect(() => { onUnread?.(unread); }, [unread]);

  return (
    <section className="mt-16" data-testid="admin-inbox">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <p className="overline-label !font-semibold !text-white/85">
          Office inbox {unread > 0 && <span className="text-accent">({unread} unread)</span>}
        </p>
        <div className="flex gap-2">
          {[["inbox", `Inbox (${messages.length - archivedCount})`], ["archived", `Archived (${archivedCount})`]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`inbox-tab-${v}`}
              onClick={() => setTab(v)}
              className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors ${tab === v ? "border-accent bg-accent text-white" : "border-white/25 text-white/60 hover:border-accent hover:text-accent"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="panel">
        {shown.length === 0 ? (
          <p className="font-display italic text-2xl text-white/40 p-6" data-testid="inbox-empty">
            {tab === "archived" ? "Nothing archived." : "No enquiries yet — the office is quiet."}
          </p>
        ) : (
          shown.map((m) => {
            const handled = m.read || (m.replies || []).length > 0 || m.archived;
            return (
              <div key={m.id} className={`px-6 py-5 border-b border-white/10 last:border-0 ${m.read && replyTo !== m.id ? "opacity-50" : ""}`} data-testid={`message-${m.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2">
                      {m.read ? <MailOpen size={14} className="text-white/40" /> : <Mail size={14} className="text-accent" />}
                      <span className="font-display text-xl">{m.name}</span>
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/40">{m.email}</span>
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">{m.message}</p>
                    {(m.replies || []).map((r, i) => (
                      <p key={i} className="text-xs text-accent/80 italic mt-3" data-testid={`reply-record-${m.id}-${i}`}>
                        You replied{r.sent_at ? ` (${new Date(r.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })})` : ""}: &ldquo;{r.body}&rdquo;
                      </p>
                    ))}
                    {replyTo === m.id && (
                      <div className="mt-4">
                        <textarea
                          data-testid={`reply-input-${m.id}`}
                          rows={4}
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          placeholder={`Write your reply to ${m.name}…`}
                          className="field resize-none mb-3"
                        />
                        <div className="flex gap-2">
                          <button
                            data-testid={`reply-send-${m.id}`}
                            onClick={() => sendReply(m)}
                            disabled={sending || !replyBody.trim()}
                            className="btn-danger !px-5 !py-2.5 disabled:opacity-40"
                          >
                            {sending ? "Sending…" : `Send to ${m.email}`}
                          </button>
                          <button onClick={() => { setReplyTo(null); setReplyBody(""); }} className="btn-outline !px-4 !py-2.5">
                            Back
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/40 mb-3">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                    </p>
                    <div className="flex flex-col gap-2 items-end">
                      {replyTo !== m.id && (
                        <button
                          data-testid={`reply-${m.id}`}
                          onClick={() => { setReplyTo(m.id); setReplyBody(""); }}
                          className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors flex items-center gap-2"
                        >
                          <Reply size={12} /> Reply
                        </button>
                      )}
                      {!m.read && (
                        <button
                          data-testid={`read-${m.id}`}
                          onClick={() => markRead(m.id)}
                          className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors"
                        >
                          Mark read
                        </button>
                      )}
                      <button
                        data-testid={`archive-${m.id}`}
                        onClick={() => toggleArchive(m)}
                        className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors flex items-center gap-2"
                      >
                        {m.archived ? <><ArchiveRestore size={12} /> Unarchive</> : <><Archive size={12} /> Archive</>}
                      </button>
                      {handled && (
                        <button
                          data-testid={`delete-message-${m.id}`}
                          onClick={() => deleteMessage(m)}
                          className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${confirmDeleteId === m.id ? "border-accent bg-accent text-white" : "border-white/25 hover:border-accent hover:text-accent"}`}
                        >
                          {confirmDeleteId === m.id ? "Sure?" : <span className="flex items-center gap-2"><Trash2 size={12} /> Delete</span>}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
